<script>
  import { format } from 'svelte-i18n';
  import { ButtonToolbar } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { AssetCard } from '@client/components/asset-card';
  import { BulkDeleteModal } from '@client/components/bulk-delete-modal';
  import { EmptyState } from '@client/components/empty-state';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { LayoutControls } from '@client/components/layout-controls';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { RenameModal } from '@client/components/rename-modal';
  import { SelectCell, SelectHeaderCell, SelectionControls } from '@client/components/selection-controls';
  import { Table } from '@client/components/table';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';
  import { documentsSettingsStore } from '@client/stores/page-settings';

  import { getExtention } from '@client/utils/files';
  import { QueryParams } from '@client/utils/query-params';
  import { createSelection, selectedItems } from '@client/utils/selection';

  import { ActionsCell, FileMetaCell, FilenameCell, FileSizeCell, FileTypeCell, PreviewCell } from './cell-formatters';

  export let refetch = false;
  export let onAddAsset = () => {};

  let renderCardLayout = $appStore.mobile || $documentsSettingsStore.cards || false;
  let sortDirection = -1;
  let sortKey = 'filename';
  let search = QueryParams.get('search') || '';
  let showFilters = true;
  let initialized = false;

  let original = [];
  let filtered = [];
  let showRenameModal = false;
  let renameDocument = null;

  let selecting = false;
  let showBulkDelete = false;

  const selection = createSelection();

  let metricsCache = {};

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const queryParams = {};
  const queryFn = async () => {
    const response = await AssetsService.getAllDocuments(queryParams);

    original = filtered = response;

    refetch = false;

    onChangeSearch(search);

    initialized = true;

    return response;
  };

  const query = createQuery({
    queryKey: ['assets-documents', queryParams],
    queryFn
  });

  const onChangeLayout = (which) => {
    renderCardLayout = which;
    $documentsSettingsStore.cards = which;
  };

  const onChangeSearch = (value) => {
    if (!value) {
      filtered = original;
    } else {
      filtered = original.filter((row) => {
        const search = value.toLowerCase();

        return Object.values(row).some((field) => {
          if (typeof field === 'string') {
            return field.toLowerCase().includes(search);
          }

          return false;
        });
      });
    }

    selection.prune(filtered);
  };

  const onRenameUpdate = async (document, newName) => {
    const response = await AssetsService.updateDocument({ ...document, rulename: newName });

    if (response?.status >= 400) {
      notify($format('label.FAILURE.DOCUMENT_RENAME', { values: { rulename: document.rulename } }), 'error');
      return;
    }

    notify($format('label.SUCCESS.RENAME', { values: { from: document.rulename, to: newName } }));

    showRenameModal = false;
    queryFn();
  };

  const onDeleteHandler = async (document) => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.DOCUMENT').toLowerCase() } })
    );

    if (confirmed) {
      $appStore.loading = true;

      const response = await AssetsService.deleteDocument(document);

      $appStore.loading = false;

      if (response?.status >= 400) {
        notify($format('label.FAILURE.RULENAME_DELETE', { values: { rulename: document.rulename } }), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE', { values: { type: document.rulename } }));

      queryFn();
    }
  };

  const onBulkDeleteFinish = async (failed) => {
    const deleted = selectedAssets.length - failed.length;

    if (deleted) {
      notify($format('label.BULK_DELETE.NOTIFY_SUCCESS', { values: { count: deleted } }));
    }

    // Leave only the failures selected so the user can retry them directly.
    if (failed.length) {
      selection.selectAll(failed);
    } else {
      selecting = false;
      selection.clear();
    }

    await queryFn();
  };

  const onClickRow = ({ detail }) => {
    if (!selecting || detail.event.target.closest('input, button, a')) {
      return;
    }

    selection.toggle(detail.row);
  };

  let columns = [
    {
      key: 'preview',
      title: $format('label.PREVIEW'),
      value: ({ filename }) => filename,
      renderComponent: PreviewCell,
      headerClass: 'preview-cell'
    },
    {
      key: 'filename',
      title: $format('label.FILENAME'),
      value: ({ filename }) => filename,
      renderComponent: FilenameCell,
      sortable: true,
      headerClass: 'filename-cell'
    },
    {
      key: 'meta',
      title: $format('label.PERFORMANCE'),
      value: ({ filesize }) => filesize,
      headerClass: 'metrics-cell',
      renderComponent: {
        component: FileMetaCell,
        props: {
          metricsCache,
          onUpdate: (cache) => {
            metricsCache = { ...cache };
          }
        }
      }
    },
    {
      key: 'filesize',
      title: $format('label.SIZE'),
      value: ({ filesize }) => filesize,
      renderComponent: FileSizeCell,
      headerClass: 'size-cell',
      sortable: true
    },
    {
      key: 'type',
      title: $format('label.TYPE'),
      value: ({ filename }) => getExtention(filename),
      renderComponent: FileTypeCell,
      headerClass: 'type-cell',
      sortable: true
    },
    {
      key: 'actions',
      title: '',
      value: '',
      renderComponent: {
        component: ActionsCell,
        props: {
          onDelete: () => {
            queryFn();
          },
          onRename: (row) => {
            renameDocument = row;
            showRenameModal = true;
          }
        }
      },
      headerClass: 'actions-cell'
    }
  ];

  $: hasDocuments = initialized && original.length > 0;
  $: $appStore.loading = $query.isLoading || $query.isFetching;
  // Rebuilt when `filtered` changes so the header checkbox tracks the visible rows.
  $: selectColumn = {
    key: 'select',
    title: { component: SelectHeaderCell, props: { selection, rows: filtered } },
    value: () => '',
    renderComponent: { component: SelectCell, props: { selection } },
    headerClass: 'select-cell'
  };
  $: tableColumns = selecting ? [selectColumn, ...columns.filter((column) => column.key !== 'actions')] : columns;
  $: selectedAssets = selectedItems(original, $selection);
  $: if (refetch) {
    queryFn();
  }
</script>

<div class="documents">
  {#if hasDocuments}
    <div class="toolbar">
      <FilterBar bind:show={showFilters}>
        <div class="toolbar-right">
          <SearchFilter bind:search onChange={onChangeSearch} />
          {#if filtered.length !== original.length}
            <small class="count">
              {$format('label.MATCHES_X_OF_X_TYPE', {
                values: {
                  filtered: Number(filtered.length).toLocaleString(),
                  total: Number(original.length).toLocaleString(),
                  type: $format('label.DOCUMENTS')
                }
              })}
            </small>
          {/if}

          <ButtonToolbar class="align-items-center gap-2">
            <SelectionControls
              bind:selecting
              {selection}
              rows={filtered}
              id="documents"
              onDelete={() => (showBulkDelete = true)}
            />

            <LayoutControls {renderCardLayout} onChange={onChangeLayout} />
          </ButtonToolbar>
        </div>
      </FilterBar>
    </div>
    <div class="items">
      {#if filtered.length}
        {#if $appStore.mobile || renderCardLayout}
          <div class="asset-cards">
            {#each filtered as document}
              <AssetCard
                bind:data={document}
                onDelete={() => onDeleteHandler(document)}
                onRename={() => {
                  renameDocument = document;
                  showRenameModal = true;
                }}
                onSelect={selection.toggle}
                selected={$selection.has(document.rulename)}
                {selecting}
                type="document"
              />
            {/each}
          </div>
        {:else}
          <Table
            id="documents-table"
            classNameThead="sticky-header"
            emptyLabel={$format('label.MESSAGE.NO_MATCHES_DOCUMENTS')}
            bind:rows={filtered}
            columns={tableColumns}
            rowClass={(row) => ($selection.has(row.rulename) ? 'bg-primary-subtle' : '')}
            on:clickRow={onClickRow}
            bind:sortBy={sortKey}
            bind:sortOrder={sortDirection}
          />
        {/if}
      {:else}
        <NoMatches message={$format('label.MESSAGE.NO_MATCHES_DOCUMENTS')} />
      {/if}
    </div>
  {:else if initialized}
    <EmptyState
      icon="file-text"
      heading={$format('label.EMPTY_STATE.DOCUMENT_HEADING')}
      description={$format('label.EMPTY_STATE.DOCUMENT_DESCRIPTION')}
      action={$format('label.EMPTY_STATE.DOCUMENT_ACTION')}
      on:click={onAddAsset}
    />
  {/if}
</div>

<RenameModal
  bind:show={showRenameModal}
  data={renameDocument}
  name={renameDocument?.rulename ?? ''}
  existingNames={original.map((d) => d.rulename)}
  duplicateError={$format('label.RENAME_MODAL.ERROR.DUPLICATE_DOCUMENT')}
  onUpdate={onRenameUpdate}
/>

<BulkDeleteModal
  bind:show={showBulkDelete}
  items={selectedAssets}
  title={$format('label.BULK_DELETE.CONFIRM_DOCUMENTS', { values: { count: selectedAssets.length } })}
  onDelete={(item) => AssetsService.deleteDocument(item)}
  onFinish={onBulkDeleteFinish}
/>

<style lang="scss" src="./documents.scss"></style>
